/* Shared male-voice reader for chatbot messages.
   Uses the browser's Web Speech API — free, no key required. Picks the deepest
   French masculine voice available. Fallback voice params are set for a bard-like
   low-pitch delivery even if only a generic voice is present. */

let cachedVoice = null;
let voicesReady = false;

function _refreshVoices() {
  try {
    const voices = window.speechSynthesis?.getVoices?.() || [];
    if (!voices.length) return;
    voicesReady = true;
    // Prefer a French male voice by name/lang. Priority order.
    const patterns = [
      /fr.*(thomas|nicolas|paul|henri|antoine|luc|male|homme)/i,
      /(thomas|nicolas|paul|henri|antoine|luc)/i,
      /fr.*male/i,
      /^fr[-_]?fr/i,
      /^fr/i,
    ];
    for (const re of patterns) {
      const hit = voices.find((v) => re.test(`${v.name} ${v.lang}`));
      if (hit) { cachedVoice = hit; return; }
    }
    cachedVoice = voices[0];
  } catch { /* ignore */ }
}

if (typeof window !== "undefined" && window.speechSynthesis) {
  _refreshVoices();
  window.speechSynthesis.onvoiceschanged = _refreshVoices;
}

/** Strip Markdown/asterisks for clearer speech synthesis. */
function _clean(text) {
  return (text || "")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/[_`]/g, "")
    .replace(/#+\s+/g, "")
    .replace(/\n{2,}/g, ". ")
    .replace(/\s+/g, " ")
    .trim();
}

/** Speak text out loud with a male-leaning French voice. Returns a stop() fn. */
export function speak(text, { lang = "fr", onEnd, onStart, onBoundary, rate = 0.94, pitch = 0.78 } = {}) {
  if (typeof window === "undefined" || !window.speechSynthesis) return () => {};
  if (!voicesReady) _refreshVoices();
  try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
  const utter = new SpeechSynthesisUtterance(_clean(text));
  utter.lang = lang === "en" ? "en-GB" : "fr-FR";
  utter.rate = rate;    // 0.94 = slightly slower than normal for gravitas
  utter.pitch = pitch;  // 0.78 = clearly masculine, avoids "chipmunk" fallback
  utter.volume = 1.0;
  if (cachedVoice && (cachedVoice.lang || "").toLowerCase().startsWith(lang)) {
    utter.voice = cachedVoice;
  }
  if (onStart) utter.onstart = onStart;
  if (onBoundary) utter.onboundary = onBoundary;
  if (onEnd) {
    utter.onend = onEnd;
    utter.onerror = onEnd;
  }
  try { window.speechSynthesis.speak(utter); } catch { /* ignore */ }
  return () => { try { window.speechSynthesis.cancel(); } catch { /* ignore */ } };
}

/** Stop any ongoing speech. */
export function stopSpeaking() {
  try { window.speechSynthesis?.cancel(); } catch { /* ignore */ }
}

export const isSpeechSupported = () =>
  typeof window !== "undefined" && !!window.speechSynthesis;


/* ----------------------------------------------------------------------
   Speech recognition (microphone → text). Web Speech API, free, works in
   Chrome / Edge / Safari (not Firefox).
   ---------------------------------------------------------------------- */

function _getRecognitionCtor() {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

export const isRecognitionSupported = () => !!_getRecognitionCtor();

/** Start listening. Returns { stop(), abort() }.
 * callbacks: { onResult(text, isFinal), onEnd(), onError(err), onStart() } */
export function listen({ lang = "fr", continuous = false, interimResults = true,
                        onResult, onEnd, onError, onStart } = {}) {
  const Ctor = _getRecognitionCtor();
  if (!Ctor) {
    onError && onError(new Error("not-supported"));
    return { stop: () => {}, abort: () => {} };
  }
  const rec = new Ctor();
  rec.lang = lang === "en" ? "en-GB" : "fr-FR";
  rec.continuous = continuous;
  rec.interimResults = interimResults;
  rec.maxAlternatives = 1;

  if (onStart) rec.onstart = onStart;
  rec.onresult = (ev) => {
    if (!onResult) return;
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const res = ev.results[i];
      const text = res[0]?.transcript || "";
      onResult(text, res.isFinal);
    }
  };
  rec.onend = () => onEnd && onEnd();
  rec.onerror = (ev) => onError && onError(ev);

  try { rec.start(); } catch (e) { onError && onError(e); }
  return {
    stop: () => { try { rec.stop(); } catch { /* ignore */ } },
    abort: () => { try { rec.abort(); } catch { /* ignore */ } },
  };
}
